export interface RecommendedAgency {
  name: string
  rosterUrl: string
  logoUrl?: string
}

export interface AgencyCategory {
  category: string
  description: string
  agencies: RecommendedAgency[]
}

export interface ResourceGuide {
  slug: string
  title: string
  summary: string
  readingTime: string
  topics: string[]
}

const ROSTER_BASE = 'https://app.zanoplatform.com/roster?agency='

const LOGOS = {
  clicksTalent: 'https://cdn.magicpatterns.com/uploads/uPKGykkxUfzE2Be8nUDjxW/clicks.jpg',
  uta: 'https://cdn.magicpatterns.com/uploads/wMgfPXQPLJcYarLDDpkiK3/uta.png',
  viralNation: 'https://cdn.magicpatterns.com/uploads/tN99MjHy7XmmJVuBG7k9RP/viral_nation_agency_logo.png',
  connectManagement: 'https://cdn.magicpatterns.com/uploads/i3Kok2sx2sbKwdR2i3GUQ9/connectmgmt.png',
  famfluence: 'https://cdn.magicpatterns.com/uploads/r6xuwZEX2dgikbJ1WTVnee/famfluence_logo_.png',
  zebedee: 'https://cdn.magicpatterns.com/uploads/2MNL5SiNoNNNw5dTs1Mk6D/zebedee_logo_.png',
  alwaysAlpha: 'https://cdn.magicpatterns.com/uploads/1CTF9A6RwhrmAFDWCuLxkM/always_alpha_logo.png',
  riseSports: 'https://cdn.magicpatterns.com/uploads/j9WrVWX1v83dK9cdnK8nCv/rise_sports_logo.png',
  nocturnalSports: 'https://cdn.magicpatterns.com/uploads/6ZeXTXae45nb38L3b8bcW6/noctural_sports_logo.png',
  eagSports: 'https://cdn.magicpatterns.com/uploads/xhQG1eLDmfphWZw7Vf5STK/EAG_Logo_.png',
  raymondRepresentation: 'https://cdn.magicpatterns.com/uploads/vARY5FL83cATbay7TTfkWQ/Raymond_Rep_Logo.png',
  dmg: 'https://cdn.magicpatterns.com/uploads/6EjCY9hZF3eDDNRH2Hfvd2/dmg_logo.png',
  inSaneSrl: 'https://cdn.magicpatterns.com/uploads/1Ph6GyyTrWrfdssFtD9etd/insane_SRL_logo.png',
  inTheKnowGlobal: 'https://cdn.magicpatterns.com/uploads/9A5U3kC95YkuuB1vGh1qot/in_the_know_global_logo.png',
  sosherMedia: 'https://cdn.magicpatterns.com/uploads/6L7bzs7whXJApfatroiHds/sosher_media_logo.png',
  dinerAgency: 'https://cdn.magicpatterns.com/uploads/1iXyPTijdCdFxJiGcNNDbv/diner_agency_logo.png',
  aboveTheInfluence: 'https://cdn.magicpatterns.com/uploads/rXWn4PUnVtj9W6omYFuc3r/above_the_influence_logo.jpg',
  stir: 'https://cdn.magicpatterns.com/uploads/u3cSofg3omDhJcWbnuQCYL/stir_brands_and_entertainment_logo.png',
  pinnacleArtists: 'https://cdn.magicpatterns.com/uploads/hoAVGAWdaYQTYVqijqbPyW/pinnacle_artists_logo.png',
  xenGaming: 'https://cdn.magicpatterns.com/uploads/fLR53Q3QmaLMUNZQ1XGAjs/xen_gaming_logo.png',
  notManagement: 'https://cdn.magicpatterns.com/uploads/7mtxJ4FCF9iSMsx58UyxAc/not_management_logo.png',
  mcrAgency: 'https://cdn.magicpatterns.com/uploads/4r9yMEGVf6FEPYxvCBfTMQ/MCR_agency_logo.jpg',
  jvAgency: 'https://cdn.magicpatterns.com/uploads/3Dk69APoHHi2pLkqAxzkmt/JV_agency_logo.png',
  epicAgencyGroup: 'https://cdn.magicpatterns.com/uploads/weYoyaVjZMgFcLwykpVE4F/epic_agency_logo.png',
  loyeAndJane: 'https://cdn.magicpatterns.com/uploads/ug8Zbs3HeWfo62VkLuyjxX/loye_and_jane_logo.png',
  theCreatorSociety: 'https://cdn.magicpatterns.com/uploads/gjjrFiLyXJQZERXH9bA5Je/the_creator_society_logo.png',
  trifidMedia: 'https://cdn.magicpatterns.com/uploads/xgtMyUnMnVizLrYwUwceiE/trifid_media_logo.png',
  soundwave: 'https://cdn.magicpatterns.com/uploads/jorwscc6x8wKBUbHwo5dmk/soundwave_logo_.png',
  oneTwelveAgency: 'https://cdn.magicpatterns.com/uploads/9huowdx6erRAE8qistn7Sw/onetwelveagency_logo.png',
  inclusifyStudio: 'https://cdn.magicpatterns.com/uploads/knM7H9Fw3AC38C22NrA222/inclusify_studio_logo.png',
}

/**
 * Editor's picks — a small, handpicked set of agencies per category,
 * selected by the Zano team rather than a full directory listing.
 */
export const AGENCY_CATEGORIES: AgencyCategory[] = [
  {
    category: 'Best celebrity & enterprise talent agencies (US)',
    description: 'Leading US talent agencies representing A-list creators, celebrities and running large-scale influencer campaigns across all niches.',
    agencies: [
      { name: 'Clicks Talent', rosterUrl: `${ROSTER_BASE}97d32b65-99e4-492d-a6a5-011bff184332`, logoUrl: LOGOS.clicksTalent },
      { name: 'UTA', rosterUrl: `${ROSTER_BASE}4abba726-9afe-460e-be45-3b9319b16ec3`, logoUrl: LOGOS.uta },
      { name: 'Viral Nation', rosterUrl: `${ROSTER_BASE}4abba726-9afe-460e-be45-3b9319b16ec3`, logoUrl: LOGOS.viralNation },
    ],
  },
  {
    category: 'Best celebrity & enterprise talent agencies (UK)',
    description: 'Leading UK talent agencies representing A-list creators, celebrities and running large-scale influencer campaigns across all niches.',
    agencies: [
      { name: 'Connect Management', rosterUrl: `${ROSTER_BASE}9d7bf4ce-e33a-43f3-b37f-51abb87e2b03`, logoUrl: LOGOS.connectManagement },
    ],
  },
  {
    category: 'Best family talent agencies',
    description: 'Talent agencies specializing in family, parenting, and household creators with highly engaged communities.',
    agencies: [
      { name: 'Famfluence', rosterUrl: `${ROSTER_BASE}631ea4a6-fbb7-429d-bd4d-0d526fa1c2f1`, logoUrl: LOGOS.famfluence },
      { name: 'ZeBeDee', rosterUrl: `${ROSTER_BASE}95e8be67-0603-419d-a6e1-eb02090da157`, logoUrl: LOGOS.zebedee },
    ],
  },
  {
    category: 'Best gaming talent agencies',
    description: 'Agencies representing Twitch streamers, YouTube gamers, esports personalities, and gaming creators across the US and Europe.',
    agencies: [
      { name: 'Xen Gaming', rosterUrl: `${ROSTER_BASE}6951eba8-cdb0-4ed0-8839-96af190e012c`, logoUrl: LOGOS.xenGaming },
      { name: 'NOT Management', rosterUrl: `${ROSTER_BASE}6c4fda21-d624-4710-95cd-286827e7df53`, logoUrl: LOGOS.notManagement },
      { name: 'MCR Agency', rosterUrl: `${ROSTER_BASE}8eb687fb-b13b-41f0-8584-6e938ec8cb99`, logoUrl: LOGOS.mcrAgency },
      { name: 'JV Agency', rosterUrl: `${ROSTER_BASE}21b49250-af6d-4466-965c-a2e27de13462`, logoUrl: LOGOS.jvAgency },
    ],
  },
  {
    category: 'Best sports talent agencies',
    description: 'Talent agencies representing professional athletes and sports creators across the NFL, NBA, WNBA, soccer, tennis, volleyball, figure skating, and major events such as the Super Bowl.',
    agencies: [
      { name: 'Always Alpha', rosterUrl: `${ROSTER_BASE}df853b58-0248-4ad5-a19a-0a10ad33cda4`, logoUrl: LOGOS.alwaysAlpha },
      { name: 'Rise Sports', rosterUrl: `${ROSTER_BASE}437b2e44-e7f1-41ab-b7cd-76a1532b3cad`, logoUrl: LOGOS.riseSports },
      { name: 'Nocturnal Sports', rosterUrl: `${ROSTER_BASE}8ffbd8c5-91a7-4051-9829-4236392225b1`, logoUrl: LOGOS.nocturnalSports },
      { name: 'EAG Sports Management', rosterUrl: `${ROSTER_BASE}b4c5d449-f41e-4475-8fe9-a5a0c69fe519`, logoUrl: LOGOS.eagSports },
      { name: 'Raymond Representation', rosterUrl: `${ROSTER_BASE}c4070f33-95bd-4e83-8e5a-e6b04e848eb4`, logoUrl: LOGOS.raymondRepresentation },
    ],
  },
  {
    category: 'Best inclusive & disability talent agencies',
    description: 'Agencies representing Deaf, hard-of-hearing, disabled, and accessibility-focused creators helping brands build more inclusive campaigns.',
    agencies: [
      { name: 'ZeBeDee', rosterUrl: `${ROSTER_BASE}95e8be67-0603-419d-a6e1-eb02090da157`, logoUrl: LOGOS.zebedee },
      { name: 'Inclusify Studio', rosterUrl: `${ROSTER_BASE}20c291e0-eb11-486e-ac3f-f8ebf1d54fba`, logoUrl: LOGOS.inclusifyStudio },
    ],
  },
  {
    category: 'Best beauty talent agencies',
    description: 'Leading beauty talent agencies representing premium creators across skincare, makeup, haircare, wellness, and luxury beauty.',
    agencies: [
      { name: 'DMG', rosterUrl: `${ROSTER_BASE}2dc99f79-ec20-42ff-b339-853f285c72fd`, logoUrl: LOGOS.dmg },
      { name: 'Glisten MGMT', rosterUrl: `${ROSTER_BASE}e4e26342-1f1a-42ac-af51-4b7a915e2673` },
      { name: 'In-Sane SRL', rosterUrl: `${ROSTER_BASE}328584e2-d8e5-4147-913b-abfa765d68fe`, logoUrl: LOGOS.inSaneSrl },
      { name: 'In the Know Global', rosterUrl: `${ROSTER_BASE}2399e472-9f57-469e-8977-c8fbc99c9d58`, logoUrl: LOGOS.inTheKnowGlobal },
      { name: 'SoSher Media', rosterUrl: `${ROSTER_BASE}13031a99-4d07-42dd-a0d2-05bd484344f4`, logoUrl: LOGOS.sosherMedia },
    ],
  },
  {
    category: 'Best fashion talent agencies',
    description: 'Agencies representing fashion and lifestyle creators across luxury, streetwear, and contemporary style in the US and Europe.',
    agencies: [
      { name: 'Epic Agency Group', rosterUrl: `${ROSTER_BASE}41a18ac0-f093-495a-bcf2-b138769c5f97`, logoUrl: LOGOS.epicAgencyGroup },
      { name: 'Loye & Jane', rosterUrl: `${ROSTER_BASE}f6b68911-4ec3-4772-a8be-865b431ebe41`, logoUrl: LOGOS.loyeAndJane },
      { name: 'The Creator Society', rosterUrl: `${ROSTER_BASE}dac06da9-19f4-4919-8b11-07788210cf65`, logoUrl: LOGOS.theCreatorSociety },
      { name: 'Trifid Media', rosterUrl: `${ROSTER_BASE}30c8a5fa-a2e6-432c-9fa0-64e9fafc6432`, logoUrl: LOGOS.trifidMedia },
    ],
  },
  {
    category: 'Best podcast talent agencies',
    description: 'Talent agencies representing podcast hosts and creators across business, entertainment, sports, lifestyle, and culture.',
    agencies: [
      { name: 'Soundwave', rosterUrl: `${ROSTER_BASE}490582f6-0f94-4e07-b590-607c0923ddea`, logoUrl: LOGOS.soundwave },
    ],
  },
  {
    category: 'Best B2B talent agencies',
    description: 'Talent agencies representing B2B creators, LinkedIn thought leaders, founders, and industry experts.',
    agencies: [
      { name: 'One Twelve Agency (UK)', rosterUrl: `${ROSTER_BASE}291f6e4c-cacd-4754-bd28-6d6cf5123a17`, logoUrl: LOGOS.oneTwelveAgency },
    ],
  },
  {
    category: 'Best general creator roster agencies',
    description: 'Well-rounded talent agencies representing creators across lifestyle, comedy, travel, food, entertainment, and many other niches.',
    agencies: [
      { name: 'Diner Agency', rosterUrl: `${ROSTER_BASE}2922dcad-fc70-4920-bc9d-830b8212e0b4`, logoUrl: LOGOS.dinerAgency },
      { name: 'Above the Influence', rosterUrl: `${ROSTER_BASE}7884a490-3037-4144-972d-2e8552099b93`, logoUrl: LOGOS.aboveTheInfluence },
      { name: 'Stir, Brands & Entertainment', rosterUrl: `${ROSTER_BASE}7057c632-22e6-4bf1-9b00-aef33f67bd12`, logoUrl: LOGOS.stir },
      { name: 'Pinnacle Artists', rosterUrl: `${ROSTER_BASE}a125cc39-f45e-45f1-91b6-0b356849ea78`, logoUrl: LOGOS.pinnacleArtists },
    ],
  },
]

export const RESOURCE_GUIDES: ResourceGuide[] = [
  {
    slug: 'what-is-a-talent-agency-roster',
    title: 'What is a talent agency roster?',
    summary: 'How influencer representation works, what sits inside a roster, and why live rosters change creator discovery.',
    readingTime: '8 min read',
    topics: ['Rosters', 'Representation', 'Creator data'],
  },
  {
    slug: 'how-brands-work-with-talent-agencies',
    title: 'How brands work with talent agencies',
    summary: 'Why agency partnerships bring reliability, clearer negotiation, better metrics, and reporting that scales.',
    readingTime: '8 min read',
    topics: ['Process', 'Campaign workflow', 'Reporting'],
  },
  {
    slug: 'talent-agencies-vs-marketing-agencies',
    title: 'Talent agencies vs. marketing agencies',
    summary: 'Where each partner fits, what they own, and why strategy plus access to the right talent decides campaign results.',
    readingTime: '7 min read',
    topics: ['Roles', 'Partnerships', 'Strategy'],
  },
]
